import { useCallback, useEffect, useRef, useState } from 'react';
import {
  JobDetailResponse,
  ScoutJobDetailResponse,
} from '@doda/api-client/aspida/@types';
import { useAppSelector } from '@doda/pc/src/redux/hooks';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { tabSelector } from '@doda/pc/src/redux/selectors/queryParams';
import { TAB_PHOTOS } from '@doda/common/src/constants/properties/const';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import { TManuscriptSummaryAbGroup } from '@doda/common/src/redux/features/abTest';
import {
  measureAbTestTManuscriptSummary,
  measureAbTestForManuscriptE,
  measureImpression,
  measureImpressionPublicationPhotoGtm,
  measureImpressionTManuscriptSummary,
} from '../../logics/analytics';
import { Tab } from '../../types/tab';

/**
 * 求人詳細 PV 計測
 * @param job 求人詳細情報
 * @param isScout スカウト求人かどうか
 * @param initialTab 初期選択タブ
 * @param isCasualInterview
 */
export const useImpressionAnalytics = (
  job: JobDetailResponse | ScoutJobDetailResponse,
  isScout: boolean,
  initialTab: Tab,
  isCasualInterview?: boolean
) => {
  const query = useAppSelector(appQueryParamsSelector);
  const tab = useAppSelector(tabSelector);
  useEffect(() => {
    if (!job) return;
    if (tab === TAB_PHOTOS) {
      // 写真タブが選択されている場合の計測処理
      measureImpressionPublicationPhotoGtm(
        job,
        query,
        isScout,
        isCasualInterview
      );
    } else {
      measureImpression(
        job,
        query,
        isScout,
        tab ?? initialTab,
        isCasualInterview
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
};

/**
 * E原稿動画化用PV計測
 * @param abTestForManuscriptEGroupNo - E原稿動画ABテストグループNo
 * @param manuscriptType - 原稿種別
 */
export const useMeasureAbTestForManuscriptE = (
  abTestForManuscriptEGroupNo: number,
  manuscriptType: string | undefined
) => {
  const isAdobeScript = useAppSelector(adobeAnalyticsScriptsSelector);
  useEffect(() => {
    if (!isAdobeScript || manuscriptType !== 'E') return;
    // PV計測より前に発火しないように発火を遅らせる
    setTimeout(() => {
      measureAbTestForManuscriptE(abTestForManuscriptEGroupNo);
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdobeScript]);
};

/**
 * DODASITE-29189 求人詳細_T原稿3点要約表示 振り分け計測
 * @param groupNo ABテストグループ
 * @param job 求人詳細情報
 * @param isScout スカウト求人かどうか
 * @param isCasualInterview カジュアル面談か
 */
export const useMeasureAbTestTManuscriptSummary = (
  groupNo: TManuscriptSummaryAbGroup,
  job: JobDetailResponse | ScoutJobDetailResponse,
  isScout: boolean,
  isCasualInterview?: boolean
) => {
  const query = useAppSelector(appQueryParamsSelector);
  const isAdobeScript = useAppSelector(adobeAnalyticsScriptsSelector);
  const jid = job?.job?.jid ?? '';
  useEffect(() => {
    if (!isAdobeScript || groupNo === 'N/A' || !jid) return;
    // PV計測より前に発火しないように発火を遅らせる
    setTimeout(() => {
      measureAbTestTManuscriptSummary(
        groupNo,
        job,
        query,
        isScout,
        isCasualInterview
      );
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdobeScript, jid, groupNo]);
};

/**
 * DODASITE-29189 求人詳細_T原稿3点要約表示 3点要約エリアのimp計測
 * 1回の画面表示につき1回だけ計測する（タブの切り替えでエリアが出直しても再計測しない）
 * @param isTarget 計測対象か（T原稿かつTEST群で3点要約を表示しているか）
 * @param job 求人詳細情報
 * @param isScout スカウト求人かどうか
 * @param isCasualInterview カジュアル面談か
 * @returns 3点要約エリアが画面に入ったときに呼ぶ関数（対象外のときはundefined）
 */
export const useMeasureImpressionTManuscriptSummary = (
  isTarget: boolean,
  job: JobDetailResponse | ScoutJobDetailResponse,
  isScout: boolean,
  isCasualInterview?: boolean
) => {
  const query = useAppSelector(appQueryParamsSelector);
  const isAdobeScript = useAppSelector(adobeAnalyticsScriptsSelector);
  const jid = job?.job?.jid ?? '';
  const measuredJidRef = useRef<string | null>(null);
  // エリアが画面に入った求人のjid
  const [visibleJid, setVisibleJid] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdobeScript || !jid || visibleJid !== jid) return;
    if (measuredJidRef.current === jid) return;
    measuredJidRef.current = jid;
    // 振り分け計測（1秒後）より後に発火させる
    setTimeout(() => {
      measureImpressionTManuscriptSummary(
        job,
        query,
        isScout,
        isCasualInterview
      );
    }, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdobeScript, visibleJid, jid]);

  const onVisible = useCallback(() => setVisibleJid(jid), [jid]);

  return isTarget ? onVisible : undefined;
};
